import * as React from 'react';

import { UploadDocumentsModal } from '@/features/estate-document/components/upload-documents-modal';
import { Copy3DModelModal } from '@/features/estate-media/components/copy-3dmodel-modal';
import { UploadDigitalTwinsModal } from '@/features/estate-media/components/media-cards/upload-digital-twins-modal';
import { ShareDigiTwinModal } from '@/features/estate-media/components/modals/share-digi-twin-modal';
import { UpdateDigiTwinModal } from '@/features/estate-media/components/modals/update-digi-twin-modal';
import { UploadMediaModal } from '@/features/estate-media/components/upload-media-modal';
import { ViewMediaModal } from '@/features/estate-media/components/view-media-modal';
import { ChooseDestinationPanoramaModal } from '@/features/estate-media/components/virtual-tour/modals/choose-destination-panorama';
import { FloorPlanModal } from '@/features/estate-media/components/virtual-tour/modals/floor-plan-modal';
import { SphericalViewerModal } from '@/features/estate-media/components/virtual-tour/modals/preview-modal';
import { ShareVirtoulTourModal } from '@/features/estate-media/components/virtual-tour/modals/share-virtual-tour-modal';
import { SelectThumbnailModal } from '@/features/estates-property/components/select-thumbnail-modal';
import { ExportImageRenderingModal } from '@/features/image-rendering/components/export-image-rendering-modal';
import { MediaLibraryModal } from '@/features/image-rendering/components/media-library-modal';
import { MobileImageUploadModal } from '@/features/image-rendering/components/mobile-image-upload-modal';
import { AddContactModal } from '@/features/tenants/components/modals/add-contact-modal';
import { AddEmployeeModal } from '@/features/tenants/components/modals/add-employee-modal';
import { AddSubtenantModal } from '@/features/tenants/components/modals/add-subtenant-modal';
import { EditEmployeeRoleModal } from '@/features/tenants/components/modals/edit-employee-role-modal';

import { ConfirmationModal } from './confirmation-modal';
import { CreateEstateModal } from './create-estate-modal';
import { EditEstateModal } from './edit-estate-modal';
import { GlobalSearchModal } from './global-search-modal';
import { EMITTER_EVENT, eventEmitter } from './modals.utils';
import {
  ModalId,
  ModalProps,
  ModalRouteProps,
  ModalsBrowserProps,
  ModalsCallArgs,
} from './modals-root.types';

const ModalsBrowser: React.FC<ModalsBrowserProps> = ({ id, children, modalProps }) => {
  return React.Children.map(children, (child) => {
    const { id: childId, Modal } = child.props;
    if (childId === id) {
      return <Modal key={id} {...modalProps} />;
    }
    return null;
  });
};

const ModalRoute: React.FC<ModalRouteProps> = () => {
  return null;
};

export const ModalsRoot: React.FC = () => {
  const [modalId, setModalId] = React.useState<ModalId | ''>('');
  const [modalProps, setModalProps] = React.useState<ModalProps>();

  const setCurrentModalHandle = (...args: ModalsCallArgs) => {
    const [id, modalProps] = args;
    if (modalProps) setModalProps(modalProps);
    return setModalId(id);
  };

  React.useEffect(() => {
    eventEmitter.addListener(EMITTER_EVENT, setCurrentModalHandle);
    return () => {
      eventEmitter.removeListener(EMITTER_EVENT, setCurrentModalHandle);
    };
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (!modalId) {
      document.body.style.overflow = 'scroll';
    }
  }, [modalId]);

  if (!modalId) return null;

  return (
    <ModalsBrowser id={modalId} modalProps={modalProps as ModalProps}>
      <ModalRoute id="confirmation-modal" Modal={ConfirmationModal} />
      <ModalRoute id="edit-employee-role" Modal={EditEmployeeRoleModal} />
      <ModalRoute id="add-employee" Modal={AddEmployeeModal} />
      <ModalRoute id="add-contact" Modal={AddContactModal} />
      <ModalRoute id="edit-estate" Modal={EditEstateModal} />
      <ModalRoute id="create-estate" Modal={CreateEstateModal} />
      <ModalRoute id="export-image-rendering-modal" Modal={ExportImageRenderingModal} />
      <ModalRoute id="upload-documents" Modal={UploadDocumentsModal} />
      <ModalRoute id="upload-media" Modal={UploadMediaModal} />
      <ModalRoute id="upload-digital-twins" Modal={UploadDigitalTwinsModal} />
      <ModalRoute id="select-thumbnail" Modal={SelectThumbnailModal} />
      <ModalRoute id="spherical-image-preview" Modal={SphericalViewerModal} />
      <ModalRoute id="floor-plan" Modal={FloorPlanModal} />
      <ModalRoute id="share-virtual-tour" Modal={ShareVirtoulTourModal} />
      <ModalRoute id="choose-destination-panorama" Modal={ChooseDestinationPanoramaModal} />
      <ModalRoute id="global-search" Modal={GlobalSearchModal} />
      <ModalRoute id="view-media" Modal={ViewMediaModal} />
      <ModalRoute id="copy-3dmodel" Modal={Copy3DModelModal} />
      <ModalRoute id="update-digi-twin" Modal={UpdateDigiTwinModal} />
      <ModalRoute id="add-subtenant" Modal={AddSubtenantModal} />
      <ModalRoute id="media-library" Modal={MediaLibraryModal} />
      <ModalRoute id="mobile-image-upload" Modal={MobileImageUploadModal} />
      <ModalRoute id="share-digi-twin" Modal={ShareDigiTwinModal} />
    </ModalsBrowser>
  );
};
